import React, { useRef, useEffect } from 'react';
import { Paper, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { NumericMetricInfo } from '@moderepo/mode-apis';
import { ChartValueType } from '../../containers/robot-cloud/RCUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            height                      : '100%',
            padding                     : theme.spacing(1),
            display                     : 'flex',
            flexDirection               : 'column',
            alignItems                  : 'center',
            justifyContent              : 'center',
            [theme.breakpoints.up('md')]: {
                padding: theme.spacing(1.5),
            },
        },

        metricValue: {
            display       : 'flex',
            flexDirection : 'column',
            flex          : 1,
            alignItems    : 'center',
            justifyContent: 'flex-start',
            width         : '100%',
            height        : '100%',
        },

        chart: {
            width    : 200,
            minHeight: 200,
            height   : '100%',
        },

        metricValueName: {
            color                       : theme.palette.text.secondary,
            fontWeight                  : 500,
            fontSize                    : 16,
            textAlign                   : 'center',
            [theme.breakpoints.up('md')]: {
                fontSize: 18,
            },
        },

        metricValueUnit: {
            paddingLeft: 5,
        },
    };
});


/**
 * Pre-build the set of gradient color from green to red
 */
const gradientColors: readonly am4core.Color[] = (() => {
    const result: am4core.Color[] = [];
    let red = 0;
    let green = 0xff;

    // Go from green to yellow by adding red
    while (red <= 0xFF) {
        result.push(am4core.color(`rgb(${red}, 255, 0)`));
        red += 1;
    }

    // Go from yellow to red by subtracting green
    while (green >= 0) {
        result.push(am4core.color(`rgb(255, ${green}, 0)`));
        green -= 1;
    }

    return result;
})();



export interface PercentChartStyleOptions {
    readonly cylinderColor?: string;
    readonly cylinderWidth?: number;
}



/**
 *
 * @param chart
 * @param isReverse
 */
const createSeries = (
    chart: am4charts.XYChart3D, fieldName: string, options: PercentChartStyleOptions | undefined,
): any => {

    // Create series
    const series1 = chart.series.push(new am4charts.ConeSeries());
    series1.dataFields.valueY = fieldName;
    series1.dataFields.categoryX = 'displayField';
    series1.columns.template.width = am4core.percent(100);
    series1.columns.template.fill = am4core.color(options?.cylinderColor || '#00b8aa');
    series1.columns.template.stroke = am4core.color(options?.cylinderColor || '#00b8aa');
    series1.columns.template.fillOpacity = 1;
    series1.columns.template.strokeOpacity = 1;
    series1.columns.template.strokeWidth = 2;


    const series2 = chart.series.push(new am4charts.ConeSeries());
    series2.dataFields.valueY = `${fieldName}_inv`;
    series2.dataFields.categoryX = 'displayField';
    series2.stacked = true;
    series2.columns.template.width = am4core.percent(100);
    series2.columns.template.fill = am4core.color('#000');
    series2.columns.template.fillOpacity = 0.1;
    series2.columns.template.stroke = am4core.color('#000');
    series2.columns.template.strokeOpacity = 0.1;
    series2.columns.template.strokeWidth = 1;
};


interface PercentMetricValueDisplayProps {
    readonly className?: string;
    readonly numericMetric: NumericMetricInfo;
    readonly value: number;
    readonly displayValue: string;
    readonly valueName: string;
    readonly type: ChartValueType;
    readonly style?: PercentChartStyleOptions;
}


/**
 * This is a Component that use a Gauge to display metric value. There are a few different type of Gauge
 *
 * @param props
 */
export const PercentMetricValueDisplay = (props: PercentMetricValueDisplayProps) => {
    const chartRef = useRef<am4charts.XYChart3D>();
    const valueLabelRef = useRef<am4core.Label>();
    const displayFieldRef = useRef<string>();
    const metricRef = useRef<NumericMetricInfo>();
    const typeRef = useRef<ChartValueType>();
    const optionsRef = useRef<PercentChartStyleOptions>();
    const classes = useStyle();



    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.XYChart3D;
            if (chart && !chart.isDisposed()) {
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };

    }, []);



    /**
     * This useEffect is used for creating/updating the chart. This useEffect depends on a lot of props and when each props change, this useEffect
     * will be called. So, it will be very slow if we were to re-create the chart every time this useEffect is called. We can't remove any of the
     * dependencies because the chart needs all the dependencies. So to solve this problem, we will keep a reference of the PREVIOUS prop values.
     * Each time this useEffect is called, we will compare the new prop values with the previous to determine which props changed. Some props
     * will always require a new chart to be created e.g. displayFields when they are changed. Some props does not which mean we can
     * just update the chart's properties when component props changed.
     */
    useEffect(() => {

        let chart = chartRef.current as am4charts.XYChart3D;

        // We will need to re-create chart if chartId changed or displayMetrics changed
        const createNewChart: boolean = displayFieldRef.current !== props.valueName;

        if (!chart || createNewChart) {

            if (chart && !chart.isDisposed()) {
                // Dispose old chart first
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }

            // create new Chart
            chart = am4core.create(props.valueName, am4charts.XYChart3D);


            const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = 'displayField';
            categoryAxis.renderer.grid.template.location = 0;
            categoryAxis.renderer.grid.template.strokeOpacity = 0;
            categoryAxis.renderer.minGridDistance = 100;
            categoryAxis.renderer.labels.template.disabled = true;

            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.grid.template.strokeOpacity = 0;
            valueAxis.min = -10;
            valueAxis.max = 120;
            valueAxis.strictMinMax = true;
            valueAxis.renderer.baseGrid.disabled = true;
            valueAxis.renderer.labels.template.adapter.add('text', (value: string | undefined) => {
                if (value) {
                    if ((Number(value) > 100) || (Number(value) < 0)) {
                        return '';
                    }
                    return `${value}%`;
                }
                return '';
            });



            // Create a label for displaying the value
            const label = chart.seriesContainer.createChild(am4core.Label);
            label.isMeasured = false;
            label.fontSize = 30;
            label.fontWeight = 'bold';
            label.x = am4core.percent(50);
            label.horizontalCenter = 'middle';
            label.verticalCenter = 'top';        // center the value vertically and horizontally
            label.y = -25;                       // Move the value down some pixel
            label.text = '0';
            valueLabelRef.current = label;


            // Add default value
            chart.data = [{
                displayField              : props.valueName,
                [props.valueName]         : 0,
                [`${props.valueName}_inv`]: 100,
            }];
        }



        if (createNewChart || displayFieldRef.current !== props.valueName || metricRef.current !== props.numericMetric
            || typeRef.current !== props.type || optionsRef.current !== props.style) {

            // remove old series
            while (chart.series.length > 0) {
                chart.series.removeIndex(0).dispose();
            }

            // Create new series with the new config
            createSeries(chart, props.valueName, props.style);
        }



        // Update the refs so that we can compare them again later
        displayFieldRef.current = props.valueName;
        metricRef.current = props.numericMetric;
        typeRef.current = props.type;
        optionsRef.current = props.style;
        chartRef.current = chart;
    }, [props.valueName, props.numericMetric, props.type, props.style]);


    /**
     * This useEffect is for monitoring the props.data value change to update the chart hand and value display.
     */
    useEffect(() => {
        const chart = chartRef.current as am4charts.XYChart3D;
        if (chart) {

            const value = Math.min(100, props.value);

            // We need to make sure value is between 0 and 100 so that the cylinder can be rendered correctly
            const fixedValue = Math.max(0, Math.min(100, value));
            chart.data = [{
                displayField              : props.valueName,
                [props.valueName]         : fixedValue,
                [`${props.valueName}_inv`]: 100 - fixedValue,
            }];

            // For label display, we can show the actual value doesn't matter if it is over or under the min/max
            if (valueLabelRef.current) {
                valueLabelRef.current.text = `${props.displayValue}%`;
            }

            // Update the cylinder fill color base on the current value
            const series = chart.series.getIndex(0) as am4charts.ConeSeries;
            if ((props.type === ChartValueType.NEGATIVE_TO_POSITIVE || props.type === ChartValueType.POSITIVE_TO_NEGATIVE) && series) {
                const colorIndex = props.type === ChartValueType.NEGATIVE_TO_POSITIVE
                    ? Math.max(0, Math.min(gradientColors.length - 1, Math.floor(((100 - fixedValue) / 100) * gradientColors.length)))
                    : Math.max(0, Math.min(gradientColors.length - 1, Math.floor((fixedValue / 100) * gradientColors.length)));

                series.columns.template.fill = gradientColors[colorIndex];
                series.columns.template.stroke = gradientColors[colorIndex];
                series.invalidate();
            }
        }
    }, [props.value, props.valueName, props.displayValue, props.type]);



    return (
        <Paper className={clsx(classes.root, props.className)}>
            <div className={classes.metricValue}>
                <div className={classes.chart} id={props.valueName} />
            </div>
            <div className={classes.metricValueName}>
                {props.numericMetric ? props.numericMetric.displayName || props.numericMetric.name : props.valueName}
                {props.numericMetric && props.numericMetric.unit && (
                    <span className={classes.metricValueUnit}>[{props.numericMetric.unit}]</span>
                )}
            </div>
        </Paper>
    );
};
